import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Filter, TrendingUp, TrendingDown, Target } from 'lucide-react';

const Indicators = () => {
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFramework, setSelectedFramework] = useState('ALL');
  const [selectedCategory, setSelectedCategory] = useState('ALL');

  const fetchIndicators = async () => {
    let url = `${API}/indicators`;
    const params = [];
    if (selectedFramework !== 'ALL') params.push(`framework=${selectedFramework}`);
    if (selectedCategory !== 'ALL') params.push(`category=${selectedCategory}`);
    if (params.length > 0) url += `?${params.join('&')}`;

    const data = await apiFetch(url);
    if (data?._error) {
      console.error('Error fetching indicators:', data.message);
      setLoading(false);
      return;
    }
    setIndicators(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchIndicators();
  }, [selectedFramework, selectedCategory]); // eslint-disable-line

  const categoryColors = {
    E1: 'blue', E2: 'cyan', E3: 'teal', E4: 'green', E5: 'lime',
    S1: 'purple', S2: 'pink', S3: 'rose', S4: 'fuchsia',
    G1: 'slate'
  };

  const categoryNames = {
    E1: 'Climate Change', E2: 'Pollution', E3: 'Water & Marine', E4: 'Biodiversity', E5: 'Circular Economy',
    S1: 'Own Workforce', S2: 'Value Chain Workers', S3: 'Affected Communities', S4: 'Consumers',
    G1: 'Business Conduct'
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="indicators-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">ESRS Indicators</h1>
        <p className="text-slate-600">Key Performance Indicators across E, S, and G pillars</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 flex gap-4 items-center">
        <Filter className="text-slate-400" size={20} />
        <div className="flex gap-4 flex-1">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">Framework</label>
            <select
              value={selectedFramework}
              onChange={(e) => setSelectedFramework(e.target.value)}
              className="border border-slate-300 rounded px-3 py-2 text-sm"
            >
              <option value="ALL">All Frameworks</option>
              <option value="ESRS">ESRS</option>
              <option value="TCFD">TCFD</option>
              <option value="GRI">GRI</option>
              <option value="SASB">SASB</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase mb-1 block">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-slate-300 rounded px-3 py-2 text-sm"
            >
              <option value="ALL">All Categories</option>
              <option value="E1">E1 - Climate</option>
              <option value="E2">E2 - Pollution</option>
              <option value="E3">E3 - Water</option>
              <option value="E4">E4 - Biodiversity</option>
              <option value="E5">E5 - Circular Economy</option>
              <option value="S1">S1 - Workforce</option>
              <option value="S2">S2 - Value Chain</option>
              <option value="S3">S3 - Communities</option>
              <option value="S4">S4 - Consumers</option>
              <option value="G1">G1 - Governance</option>
            </select>
          </div>
        </div>
        <div className="text-sm text-slate-600">
          Showing <strong>{indicators.length}</strong> indicators
        </div>
      </div>

      {/* Indicators Grid */}
      <div className="grid grid-cols-1 gap-4">
        {indicators.map((indicator) => {
          const color = categoryColors[indicator.category];
          const hasTarget = indicator.target_value !== null && indicator.current_value !== null;
          const progress = hasTarget ? (indicator.current_value / indicator.target_value * 100) : 0;
          const onTrack = hasTarget ? progress <= 100 : null;
          
          return (
            <div key={indicator.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 bg-${color}-100 text-${color}-800 text-xs font-bold rounded`}>
                      {indicator.code}
                    </span>
                    <span className="text-xs text-slate-500">{categoryNames[indicator.category]}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{indicator.name}</h3>
                  <p className="text-sm text-slate-600">{indicator.description}</p>
                </div>
                {hasTarget && (
                  <div className="ml-4">
                    {onTrack ? (
                      <TrendingUp className="text-green-600" size={32} />
                    ) : (
                      <TrendingDown className="text-red-600" size={32} />
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase">Type</span>
                  <p className="text-sm text-slate-700 capitalize">{indicator.metric_type}</p>
                </div>
                {indicator.unit && (
                  <div>
                    <span className="text-xs font-semibold text-slate-500 uppercase">Unit</span>
                    <p className="text-sm text-slate-700">{indicator.unit}</p>
                  </div>
                )}
              </div>

              {hasTarget && (
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-slate-700">Progress to Target</span>
                    <span className="text-sm font-bold text-slate-900">
                      {indicator.current_value} / {indicator.target_value} {indicator.unit}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2.5">
                    <div
                      className={`h-2.5 rounded-full transition-all ${onTrack ? 'bg-green-600' : 'bg-red-600'}`}
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase">Frameworks</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {indicator.frameworks.map((fw) => (
                    <span key={fw} className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded">
                      {fw}
                    </span>
                  ))}
                </div>
                
                {Object.keys(indicator.crosswalk_mappings).length > 0 && (
                  <div>
                    <span className="text-xs font-semibold text-slate-500 uppercase">Crosswalk Mappings</span>
                    <div className="mt-1 space-y-1">
                      {Object.entries(indicator.crosswalk_mappings).map(([fw, mapping]) => (
                        <div key={fw} className="text-xs text-slate-600">
                          <strong>{fw}:</strong> {mapping}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Indicators;